import React, { useEffect } from 'react'

// --- Google Tag Manager dynamic loader ---
function ensureDataLayer(name = 'dataLayer'): unknown[] {
  const w = window as unknown as { [k: string]: unknown }
  if (!Array.isArray(w[name] as unknown[])) {
    // eslint-disable-next-line no-param-reassign
    w[name] = []
  }
  return w[name] as unknown[]
}

function loadGtm(id: string, dataLayerName = 'dataLayer'): void {
  if (typeof window === 'undefined' || !id) return
  // Avoid re-injecting the same container
  if (document.querySelector(`script[data-gtm-id="${id}"]`)) return

  const dl = ensureDataLayer(dataLayerName)
  dl.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

  const s = document.createElement('script')
  s.async = true
  s.src =
    `https://www.googletagmanager.com/gtm.js?id=${id}` +
    (dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : '')
  s.setAttribute('data-gtm-id', id)
  document.head.appendChild(s)
}

function unloadGtm(id: string): void {
  if (typeof window === 'undefined' || !id) return
  document
    .querySelectorAll(`script[data-gtm-id="${id}"]`)
    .forEach((el) => el.parentNode?.removeChild(el))
  // Remove noscript iframe if present (we don't add it, but be defensive)
  document
    .querySelectorAll(`iframe[src*="googletagmanager.com/ns.html?id=${id}"]`)
    .forEach((el) => el.parentNode?.removeChild(el))
}

export default function useDynamicGtm(id?: string): void {
  const prevRef = React.useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!id) return
    if (prevRef.current && prevRef.current !== id) {
      unloadGtm(prevRef.current)
    }
    loadGtm(id)
    prevRef.current = id
  }, [id])

  // Optional: cleanup on unmount
  useEffect(() => {
    return () => {
      if (prevRef.current) unloadGtm(prevRef.current)
    }
  }, [])
}
